/**
 * buildInitialPlan — the deterministic one-time plan writer.
 *
 * One intake, one plan, fully explainable. No clock, no randomness, no network:
 * identical intakes produce byte-identical plans (asserted in the tests). Every
 * number in the output is traceable to an intake value or a named rule, and the
 * `rationale[]` array carries one entry per decision family.
 *
 * The engine is gym-based and indoor. Splits contain ergs, sleds, carries, boxes,
 * medicine balls, ropes, machines, cables, barbells, dumbbells, kettlebells, bands
 * and bodyweight work — never running or an outdoor modality.
 *
 * Deload rule: week 4 of every 4-week block. The generated block is 8 weeks, so
 * weeks 4 and 8 are deloads (sets x0.6, load -10%). The weekly `days` template is
 * the same every week; `deloadWeekNumbers(plan.weeks)` derives the deload weeks and
 * `applyDeload(exercise)` produces the deload prescription for a given exercise.
 */
import { epley1rm, round2, roundToIncrement } from '../evaluation/math.js';
import {
  DELOAD_INTENSITY_FACTOR,
  DELOAD_VOLUME_FACTOR,
  MIN_DELOAD_SETS,
} from '../evaluation/progression.js';
import type {
  DaysPerWeek,
  Equipment,
  Experience,
  PlanGoal,
  PlanIntake,
  SessionMinutes,
  Sex,
  TrainingTrack,
} from './intake.js';
import { PlanIntakeSchema } from './intake.js';
import {
  EXERCISE_CATALOGUE,
  type CatalogueExercise,
  type DayStyle,
  type MovementPattern,
  type SetMeasure,
  type SelectedExercise,
  selectExercises,
  blockedExercises,
} from './exercise-selection.js';
import {
  CONDITIONING_WORK_REST,
  MAX_SETS_PER_EXERCISE,
  MIN_SETS_PER_EXERCISE,
  RESISTANCE_MUSCLES,
  WELLNESS_LANDMARKS,
  classifyVolume,
  frequencyRuleFor,
  targetWeeklySetsFor,
  trackVolumeTarget,
  volumeLandmarksFor,
  type MuscleGroup,
  type ResistanceMuscle,
  type VolumeStatus,
} from './volume-landmarks.js';

/** The default generated block length. Deloads land on weeks 4 and 8. */
export const DEFAULT_BLOCK_WEEKS = 8;
/** A deload every fourth week, as the brief requires. */
export const DELOAD_WEEK_EVERY = 4;
/** Heuristic uncertainty attached to every bodyweight-derived start load. */
export const ESTIMATE_UNCERTAINTY_PCT = 20;

export const TRACK_METRICS_FOCUS: Record<TrainingTrack, ReadonlyArray<string>> = {
  strength: ['e1rm', 'topSetLoad', 'tonnage'],
  hypertrophy: ['weeklySetsPerMuscle', 'volume', 'bodyweight'],
  conditioning: ['intervalMinutes', 'workRestRatio', 'ergMetres', 'avgRpe'],
  performance: ['powerMarkers', 'jumpHeight', 'throwDistance', 'readiness'],
  wellness: ['mobilitySessions', 'painScore', 'consistency'],
  general: ['volume', 'sessionsPerWeek', 'consistency'],
};

/** Customer-facing names. `wellness` is displayed as "Movement & Recovery". */
export const TRACK_LABELS: Record<TrainingTrack, string> = {
  strength: 'Strength',
  hypertrophy: 'Muscle & Physique',
  conditioning: 'Indoor Conditioning',
  performance: 'Indoor Athletic Performance',
  wellness: 'Movement & Recovery',
  general: 'All-Round Fitness',
};

export interface GeneratedPlanExercise {
  movement: string;
  variant: string | null;
  equipment: string;
  sets: number;
  repMin: number;
  repMax: number;
  perSide: boolean;
  measure: SetMeasure;
  targetRpe: number | null;
  targetWeightKg: number | null;
  percent1rm: number | null;
  tempo: string | null;
  restSeconds: number;
  supersetGroup: number | null;
  notes: string | null;
  /** `plan_exercises.is_bodyweight`. */
  isBodyweight: boolean;
  /** `plan_exercises.target_distance_m`. */
  targetDistanceM: number | null;
  /** `plan_exercises.target_duration_s`. */
  targetDurationS: number | null;
}

export interface GeneratedPlanDay {
  dayIndex: number;
  label: string;
  focus: string[];
  exercises: GeneratedPlanExercise[];
}

export interface GeneratedPlan {
  planId: string;
  name: string;
  goal: string;
  track: TrainingTrack;
  weeks: number;
  daysPerWeek: number;
  summaryMd: string;
  /** What the app should track for this track. */
  metricsFocus: string[];
  rationale: Array<{ rule: string; evidence: string }>;
  days: GeneratedPlanDay[];
}

export interface PlanBuildOptions {
  /**
   * Optional ISO timestamp for the "prepared" line in `summaryMd`. Omit it (the
   * default) and the plan is byte-identical for a given intake.
   */
  now?: string;
}

// --- splits ----------------------------------------------------------------

interface SplitDay {
  label: string;
  focus: ReadonlyArray<MuscleGroup>;
  style: DayStyle;
  patterns: ReadonlyArray<MovementPattern>;
}

type SplitTable = Record<DaysPerWeek, ReadonlyArray<SplitDay>>;

const UPPER: ReadonlyArray<MuscleGroup> = ['chest', 'back', 'shoulders', 'biceps', 'triceps'];
const LOWER: ReadonlyArray<MuscleGroup> = ['quads', 'hamstrings', 'glutes', 'calves', 'core'];
const PUSH: ReadonlyArray<MuscleGroup> = ['chest', 'shoulders', 'triceps', 'core'];
const PULL: ReadonlyArray<MuscleGroup> = ['back', 'biceps', 'forearms', 'core'];
const FULL: ReadonlyArray<MuscleGroup> = ['quads', 'chest', 'back', 'hamstrings', 'shoulders', 'glutes', 'core'];

/**
 * Resistance splits.
 *   2 days  full-body x2          (every muscle 2x/week)
 *   3 days  full-body x3          (every muscle 3x/week; a push/pull/legs rotation
 *                                   is a valid alternative but drops each group to
 *                                   1x/week, so it is not the default)
 *   4 days  upper/lower x2        (every muscle 2x/week)
 *   5 days  upper/lower + push/pull/legs (every muscle 2x/week)
 *   6 days  push/pull/legs x2     (every muscle 2x/week, plus a weak-point touch)
 */
const RESISTANCE_SPLITS: SplitTable = {
  2: [
    { label: 'Full Body A', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'horizontal_pull'] },
    { label: 'Full Body B', focus: FULL, style: 'resistance', patterns: ['hinge', 'vertical_push', 'vertical_pull'] },
  ],
  3: [
    { label: 'Full Body A', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'horizontal_pull'] },
    { label: 'Full Body B', focus: FULL, style: 'resistance', patterns: ['hinge', 'vertical_push', 'vertical_pull'] },
    { label: 'Full Body C', focus: FULL, style: 'resistance', patterns: ['lunge', 'horizontal_push', 'horizontal_pull', 'core'] },
  ],
  4: [
    { label: 'Upper A', focus: UPPER, style: 'resistance', patterns: ['horizontal_push', 'horizontal_pull', 'vertical_push', 'lateral_raise'] },
    { label: 'Lower A', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'calf', 'core'] },
    { label: 'Upper B', focus: UPPER, style: 'resistance', patterns: ['vertical_push', 'vertical_pull', 'horizontal_pull', 'curl'] },
    { label: 'Lower B', focus: LOWER, style: 'resistance', patterns: ['hinge', 'lunge', 'squat', 'core'] },
  ],
  5: [
    { label: 'Upper A', focus: UPPER, style: 'resistance', patterns: ['horizontal_push', 'horizontal_pull', 'vertical_push'] },
    { label: 'Lower A', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'calf'] },
    { label: 'Push', focus: PUSH, style: 'resistance', patterns: ['horizontal_push', 'vertical_push', 'triceps_extension', 'lateral_raise'] },
    { label: 'Pull', focus: PULL, style: 'resistance', patterns: ['horizontal_pull', 'vertical_pull', 'curl', 'core'] },
    { label: 'Legs', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'lunge', 'core'] },
  ],
  6: [
    { label: 'Push A', focus: PUSH, style: 'resistance', patterns: ['horizontal_push', 'vertical_push', 'lateral_raise', 'triceps_extension'] },
    { label: 'Pull A', focus: PULL, style: 'resistance', patterns: ['horizontal_pull', 'vertical_pull', 'curl'] },
    { label: 'Legs A', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'calf'] },
    { label: 'Push B', focus: PUSH, style: 'resistance', patterns: ['vertical_push', 'horizontal_push', 'triceps_extension', 'lateral_raise'] },
    { label: 'Pull B', focus: PULL, style: 'resistance', patterns: ['vertical_pull', 'horizontal_pull', 'core'] },
    { label: 'Legs B', focus: LOWER, style: 'resistance', patterns: ['hinge', 'lunge', 'squat', 'core'] },
  ],
};

/** Indoor conditioning: ergs, sleds, carries, ropes and circuits only. */
const CONDITIONING_SPLITS: SplitTable = {
  2: [
    { label: 'Intervals', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Sled & Carry', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
  ],
  3: [
    { label: 'Intervals', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Sled & Carry', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
    { label: 'Erg Capacity', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
  ],
  4: [
    { label: 'Intervals', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Sled & Carry', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
    { label: 'Erg Capacity', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Circuit', focus: ['full_body'], style: 'conditioning', patterns: ['rope', 'throw', 'core'] },
  ],
  5: [
    { label: 'Intervals', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Sled & Carry', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
    { label: 'Erg Capacity', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Circuit', focus: ['full_body'], style: 'conditioning', patterns: ['rope', 'throw', 'core'] },
    { label: 'Sled Repeats', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
  ],
  6: [
    { label: 'Intervals', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Sled & Carry', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
    { label: 'Erg Capacity', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Circuit', focus: ['full_body'], style: 'conditioning', patterns: ['rope', 'throw', 'core'] },
    { label: 'Sled Repeats', focus: ['full_body'], style: 'conditioning', patterns: ['sled', 'carry'] },
    { label: 'Movement Flush', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
  ],
};

/** Indoor performance: jumps, throws, sled accelerations and derivatives. */
const PERFORMANCE_SPLITS: SplitTable = {
  2: [
    { label: 'Power', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'throw'] },
    { label: 'Strength Base', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'hinge'] },
  ],
  3: [
    { label: 'Power', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'throw'] },
    { label: 'Explosiveness', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'sled'] },
    { label: 'Strength Base', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'hinge', 'vertical_pull'] },
  ],
  4: [
    { label: 'Power', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'throw'] },
    { label: 'Explosiveness', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'sled'] },
    { label: 'Strength Base', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'hinge', 'vertical_pull'] },
    { label: 'Energy System', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
  ],
  5: [
    { label: 'Power', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'throw'] },
    { label: 'Explosiveness', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'sled'] },
    { label: 'Strength Base', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'hinge', 'vertical_pull'] },
    { label: 'Energy System', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Movement Prep', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
  ],
  6: [
    { label: 'Power', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'throw'] },
    { label: 'Explosiveness', focus: ['full_body'], style: 'power', patterns: ['plyometric', 'sled'] },
    { label: 'Strength Base', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'hinge', 'vertical_pull'] },
    { label: 'Energy System', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio'] },
    { label: 'Movement Prep', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Power Endurance', focus: ['full_body'], style: 'power', patterns: ['throw', 'sled', 'plyometric'] },
  ],
};

/** Movement & Recovery: mobility, stability, low-impact machine/cable work. */
const WELLNESS_SPLITS: SplitTable = {
  2: [
    { label: 'Mobility Flow', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Stability & Core', focus: ['core'], style: 'mobility', patterns: ['stability', 'core'] },
  ],
  3: [
    { label: 'Mobility Flow', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Stability & Core', focus: ['core'], style: 'mobility', patterns: ['stability', 'core'] },
    { label: 'Low-Impact Cardio', focus: ['cardio'], style: 'mobility', patterns: ['machine_cardio', 'erg'] },
  ],
  4: [
    { label: 'Mobility Flow', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Stability & Core', focus: ['core'], style: 'mobility', patterns: ['stability', 'core'] },
    { label: 'Low-Impact Cardio', focus: ['cardio'], style: 'mobility', patterns: ['machine_cardio', 'erg'] },
    { label: 'Movement Quality', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
  ],
  5: [
    { label: 'Mobility Flow', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Stability & Core', focus: ['core'], style: 'mobility', patterns: ['stability', 'core'] },
    { label: 'Low-Impact Cardio', focus: ['cardio'], style: 'mobility', patterns: ['machine_cardio', 'erg'] },
    { label: 'Movement Quality', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Recovery', focus: ['full_body'], style: 'mobility', patterns: ['mobility'] },
  ],
  6: [
    { label: 'Mobility Flow', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Stability & Core', focus: ['core'], style: 'mobility', patterns: ['stability', 'core'] },
    { label: 'Low-Impact Cardio', focus: ['cardio'], style: 'mobility', patterns: ['machine_cardio', 'erg'] },
    { label: 'Movement Quality', focus: ['full_body'], style: 'mobility', patterns: ['mobility', 'stability'] },
    { label: 'Recovery', focus: ['full_body'], style: 'mobility', patterns: ['mobility'] },
    { label: 'Gentle Cardio', focus: ['cardio'], style: 'mobility', patterns: ['machine_cardio'] },
  ],
};

/** All-round fitness: resistance work plus indoor conditioning in the same week. */
const GENERAL_SPLITS: SplitTable = {
  2: [
    { label: 'Full Body', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'horizontal_pull'] },
    { label: 'Conditioning', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio', 'sled'] },
  ],
  3: [
    { label: 'Full Body A', focus: FULL, style: 'resistance', patterns: ['squat', 'horizontal_push', 'horizontal_pull'] },
    { label: 'Conditioning', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio', 'sled'] },
    { label: 'Full Body B', focus: FULL, style: 'resistance', patterns: ['hinge', 'vertical_push', 'vertical_pull'] },
  ],
  4: [
    { label: 'Upper', focus: UPPER, style: 'resistance', patterns: ['horizontal_push', 'horizontal_pull', 'vertical_push'] },
    { label: 'Lower', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'calf'] },
    { label: 'Conditioning', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio', 'sled'] },
    { label: 'Full Body', focus: FULL, style: 'resistance', patterns: ['lunge', 'vertical_pull', 'core'] },
  ],
  5: [
    { label: 'Upper', focus: UPPER, style: 'resistance', patterns: ['horizontal_push', 'horizontal_pull', 'vertical_push'] },
    { label: 'Lower', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'calf'] },
    { label: 'Conditioning', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio', 'sled'] },
    { label: 'Full Body', focus: FULL, style: 'resistance', patterns: ['lunge', 'vertical_pull', 'core'] },
    { label: 'Circuit', focus: ['full_body'], style: 'conditioning', patterns: ['rope', 'carry', 'throw'] },
  ],
  6: [
    { label: 'Upper', focus: UPPER, style: 'resistance', patterns: ['horizontal_push', 'horizontal_pull', 'vertical_push'] },
    { label: 'Lower', focus: LOWER, style: 'resistance', patterns: ['squat', 'hinge', 'calf'] },
    { label: 'Push', focus: PUSH, style: 'resistance', patterns: ['horizontal_push', 'vertical_push', 'triceps_extension'] },
    { label: 'Pull', focus: PULL, style: 'resistance', patterns: ['horizontal_pull', 'vertical_pull', 'curl'] },
    { label: 'Legs', focus: LOWER, style: 'resistance', patterns: ['squat', 'lunge', 'core'] },
    { label: 'Conditioning', focus: ['cardio'], style: 'conditioning', patterns: ['erg', 'machine_cardio', 'sled'] },
  ],
};

const SPLITS: Record<TrainingTrack, SplitTable> = {
  strength: RESISTANCE_SPLITS,
  hypertrophy: RESISTANCE_SPLITS,
  conditioning: CONDITIONING_SPLITS,
  performance: PERFORMANCE_SPLITS,
  wellness: WELLNESS_SPLITS,
  general: GENERAL_SPLITS,
};

// --- training variables ----------------------------------------------------

interface RepProfile {
  repMin: number;
  repMax: number;
  rpeMin: number;
  rpeMax: number;
  restSeconds: number;
  percent1rm: boolean;
  tempo: string | null;
  rule: string;
  note: string;
}

const GOAL_PROFILES: Record<PlanGoal, RepProfile> = {
  strength: {
    repMin: 3, repMax: 6, rpeMin: 7, rpeMax: 8, restSeconds: 210,
    percent1rm: true, tempo: null, rule: 'reps.goal_strength',
    note: 'Heavy sets, low reps, long rests.',
  },
  hypertrophy: {
    repMin: 6, repMax: 12, rpeMin: 7, rpeMax: 9, restSeconds: 90,
    percent1rm: false, tempo: '3-0-1-0', rule: 'reps.goal_hypertrophy',
    note: 'Moderate reps with a controlled lowering phase.',
  },
  fat_loss: {
    repMin: 8, repMax: 15, rpeMin: 6, rpeMax: 8, restSeconds: 60,
    percent1rm: false, tempo: '2-0-1-0', rule: 'reps.goal_fat_loss',
    note: 'Shorter rests keep the session moving.',
  },
  endurance: {
    repMin: 12, repMax: 20, rpeMin: 6, rpeMax: 8, restSeconds: 45,
    percent1rm: false, tempo: null, rule: 'reps.goal_endurance',
    note: 'Higher reps and short rests.',
  },
  general: {
    repMin: 8, repMax: 12, rpeMin: 6, rpeMax: 8, restSeconds: 90,
    percent1rm: false, tempo: null, rule: 'reps.goal_general',
    note: 'A rep range you can progress every week.',
  },
};

/** Track-first profiles; the goal only refines the `general` track. */
const TRACK_REP_PROFILES: Partial<Record<TrainingTrack, RepProfile>> = {
  strength: {
    repMin: 3, repMax: 6, rpeMin: 7, rpeMax: 8, restSeconds: 210,
    percent1rm: true, tempo: null, rule: 'reps.track_strength',
    note: 'Heavy sets, low reps, long rests.',
  },
  hypertrophy: {
    repMin: 6, repMax: 12, rpeMin: 7, rpeMax: 9, restSeconds: 90,
    percent1rm: false, tempo: '3-0-1-0', rule: 'reps.track_hypertrophy',
    note: 'Moderate reps with a controlled lowering phase.',
  },
  performance: {
    repMin: 3, repMax: 6, rpeMin: 7, rpeMax: 8, restSeconds: 180,
    percent1rm: false, tempo: null, rule: 'reps.track_performance',
    note: 'Strength base: heavy but never grinding.',
  },
  general: GOAL_PROFILES.general,
};

function resolveRepProfile(track: TrainingTrack, goal: PlanGoal): RepProfile {
  if (track === 'general') return GOAL_PROFILES[goal];
  return TRACK_REP_PROFILES[track] ?? GOAL_PROFILES.general;
}

/** RPE a lifter of this experience should actually sit at inside the band. */
function targetRpeFor(profile: RepProfile, experience: Experience): number {
  if (experience === 'beginner') return round2(profile.rpeMin);
  if (experience === 'advanced') return round2(Math.min(profile.rpeMax, profile.rpeMin + 1.5));
  return round2((profile.rpeMin + profile.rpeMax) / 2);
}

/**
 * Population start-load multipliers, expressed as a multiple of bodyweight for the
 * working load at the prescribed reps. These are coaching heuristics, not measured
 * maxima: every one carries a stated uncertainty and the first session overrides
 * them with the lifter's own RPE.
 */
const PATTERN_LOAD_MULTIPLIER: Partial<Record<MovementPattern, number>> = {
  squat: 1.0,
  hinge: 1.15,
  lunge: 0.45,
  horizontal_push: 0.8,
  vertical_push: 0.55,
  horizontal_pull: 0.7,
  vertical_pull: 0.9,
};

const ESTIMATE_EXPERIENCE_FACTOR: Record<Experience, number> = {
  beginner: 0.7,
  intermediate: 1.0,
  advanced: 1.2,
};

const ESTIMATE_SEX_FACTOR: Record<Sex, number> = {
  female: 0.7,
  male: 1.0,
  unspecified: 0.85,
};

const ESTIMABLE_EQUIPMENT: ReadonlyArray<Equipment> = ['barbell', 'machine', 'kettlebell'];

interface LoadEstimate {
  targetWeightKg: number | null;
  percent1rm: number | null;
  multiplier: number | null;
}

function estimateStartLoad(
  entry: CatalogueExercise,
  intake: PlanIntake,
  profile: RepProfile,
): LoadEstimate {
  const multiplier = PATTERN_LOAD_MULTIPLIER[entry.pattern];
  const canEstimate =
    multiplier !== undefined &&
    entry.isCompound &&
    ESTIMABLE_EQUIPMENT.includes(entry.equipment) &&
    (intake.track === 'strength' ||
      intake.track === 'hypertrophy' ||
      intake.track === 'general' ||
      intake.track === 'performance');
  if (!canEstimate || multiplier === undefined) {
    return { targetWeightKg: null, percent1rm: null, multiplier: null };
  }
  const raw =
    intake.bodyweightKg *
    multiplier *
    ESTIMATE_EXPERIENCE_FACTOR[intake.experience] *
    ESTIMATE_SEX_FACTOR[intake.sex];
  const increment = entry.loadIncrementKg > 0 ? entry.loadIncrementKg : 2.5;
  const targetWeightKg = Math.max(roundToIncrement(raw, increment), increment);
  const midReps = Math.max(1, Math.round((profile.repMin + profile.repMax) / 2));
  const estimated1rm = epley1rm(targetWeightKg, midReps);
  const percent1rm =
    profile.percent1rm && estimated1rm > 0
      ? round2((targetWeightKg / estimated1rm) * 100)
      : null;
  return { targetWeightKg, percent1rm, multiplier };
}

// --- prescription ----------------------------------------------------------

interface PrescriptionCore {
  sets: number;
  repMin: number;
  repMax: number;
  targetRpe: number | null;
  targetWeightKg: number | null;
  percent1rm: number | null;
  tempo: string | null;
  restSeconds: number;
  targetDistanceM: number | null;
  targetDurationS: number | null;
  loadMultiplier: number | null;
  note: string;
}

function setsForExercise(entry: CatalogueExercise, intake: PlanIntake): number {
  const accessoryBase =
    intake.experience === 'beginner' ? 3 : intake.sessionMinutes >= 60 ? 4 : 3;
  let base = entry.isCompound ? (intake.experience === 'beginner' ? 3 : 4) : accessoryBase;
  if (intake.sessionMinutes <= 30) base = Math.min(base, 3);
  if (intake.sessionMinutes >= 75 && entry.isCompound && intake.experience === 'advanced') base = 5;
  return Math.min(Math.max(base, MIN_SETS_PER_EXERCISE), MAX_SETS_PER_EXERCISE);
}

function roundsFor(intake: PlanIntake): number {
  if (intake.experience === 'beginner') return 5;
  if (intake.experience === 'intermediate') return 6;
  return 8;
}

function intervalDistanceM(entry: CatalogueExercise, intake: PlanIntake): number {
  const step = intake.experience === 'beginner' ? 0 : intake.experience === 'intermediate' ? 1 : 2;
  if (entry.pattern === 'sled') return [20, 20, 25][step] ?? 20;
  if (entry.pattern === 'carry') return [40, 50, 60][step] ?? 40;
  if (entry.pattern === 'erg' || entry.pattern === 'machine_cardio') {
    return [500, 750, 1000][step] ?? 500;
  }
  return [200, 300, 400][step] ?? 200;
}

function resistancePrescription(entry: CatalogueExercise, intake: PlanIntake, profile: RepProfile): PrescriptionCore {
  const targetRpe = targetRpeFor(profile, intake.experience);
  const load = estimateStartLoad(entry, intake, profile);
  if (entry.defaultMeasure === 'time_s') {
    return {
      sets: intake.experience === 'beginner' ? 2 : 3,
      repMin: 1,
      repMax: 1,
      targetRpe: round2(Math.max(5, targetRpe - 1)),
      targetWeightKg: null,
      percent1rm: null,
      tempo: null,
      restSeconds: 45,
      targetDistanceM: null,
      targetDurationS: entry.pattern === 'stability' ? 30 : 40,
      loadMultiplier: null,
      note: 'Hold the position; stop the set when the position breaks.',
    };
  }
  return {
    sets: setsForExercise(entry, intake),
    repMin: profile.repMin,
    repMax: profile.repMax,
    targetRpe,
    targetWeightKg: load.targetWeightKg,
    percent1rm: load.percent1rm,
    tempo: profile.tempo,
    restSeconds: profile.restSeconds,
    targetDistanceM: null,
    targetDurationS: null,
    loadMultiplier: load.multiplier,
    note: `${profile.note}${entry.cue ? ` ${entry.cue}` : ''}`,
  };
}

function conditioningPrescription(entry: CatalogueExercise, intake: PlanIntake): PrescriptionCore {
  const workRest = CONDITIONING_WORK_REST[intake.experience];
  const work = Math.min(Math.max(Math.round((workRest.workSecondsMin + workRest.workSecondsMax) / 2), 20), 240);
  const rest = Math.min(Math.max(Math.round((workRest.restSecondsMin + workRest.restSecondsMax) / 2), 15), 300);
  const rounds = roundsFor(intake);
  const targetRpe = intake.experience === 'beginner' ? 7 : 8;
  if (entry.defaultMeasure === 'distance_m') {
    const distance = intervalDistanceM(entry, intake);
    return {
      sets: rounds,
      repMin: 1,
      repMax: 1,
      targetRpe,
      targetWeightKg: null,
      percent1rm: null,
      tempo: null,
      restSeconds: rest,
      targetDistanceM: distance,
      targetDurationS: null,
      loadMultiplier: null,
      note: `${rounds} x ${distance} m with ${rest} s rest between efforts.`,
    };
  }
  if (entry.defaultMeasure === 'amrap') {
    const capSeconds = Math.min(Math.max(rounds * work, 360), 900);
    return {
      sets: 1,
      repMin: 1,
      repMax: 1,
      targetRpe,
      targetWeightKg: null,
      percent1rm: null,
      tempo: null,
      restSeconds: 0,
      targetDistanceM: null,
      targetDurationS: capSeconds,
      loadMultiplier: null,
      note: `AMRAP in ${Math.round(capSeconds / 60)} minutes; keep the reps clean.`,
    };
  }
  if (entry.defaultMeasure === 'time_s') {
    return {
      sets: rounds,
      repMin: 1,
      repMax: 1,
      targetRpe,
      targetWeightKg: null,
      percent1rm: null,
      tempo: null,
      restSeconds: rest,
      targetDistanceM: null,
      targetDurationS: work,
      loadMultiplier: null,
      note: `${rounds} x ${work} s work / ${rest} s rest (${workRest.ratio}).`,
    };
  }
  return {
    sets: 4,
    repMin: 8,
    repMax: 12,
    targetRpe,
    targetWeightKg: null,
    percent1rm: null,
    tempo: null,
    restSeconds: 60,
    targetDistanceM: null,
    targetDurationS: null,
    loadMultiplier: null,
    note: 'Circuit reps: move with intent, rest only as prescribed.',
  };
}

function powerPrescription(entry: CatalogueExercise, intake: PlanIntake): PrescriptionCore {
  const targetRpe = intake.experience === 'beginner' ? 6 : 7;
  if (entry.pattern === 'throw') {
    return {
      sets: 4,
      repMin: 3,
      repMax: 6,
      targetRpe,
      targetWeightKg: null,
      percent1rm: null,
      tempo: null,
      restSeconds: 90,
      targetDistanceM: null,
      targetDurationS: null,
      loadMultiplier: null,
      note: `Throw hard every rep and reset. ${entry.cue}`,
    };
  }
  if (entry.pattern === 'plyometric') {
    const load = entry.isBodyweight ? { targetWeightKg: null, percent1rm: null } : estimateStartLoad(entry, intake, GOAL_PROFILES.strength);
    return {
      sets: 4,
      repMin: 1,
      repMax: 5,
      targetRpe,
      targetWeightKg: load.targetWeightKg,
      percent1rm: null,
      tempo: 'X-0-X-0',
      restSeconds: 120,
      targetDistanceM: null,
      targetDurationS: null,
      loadMultiplier: null,
      note: `Explosive intent, full rest between sets. ${entry.cue}`,
    };
  }
  // Strength-base work inside a performance plan.
  const profile = GOAL_PROFILES.strength;
  const core = resistancePrescription(entry, intake, profile);
  return core;
}

function wellnessPrescription(entry: CatalogueExercise, intake: PlanIntake): PrescriptionCore {
  const landmark = WELLNESS_LANDMARKS[intake.experience];
  const targetRpe = round2((landmark.rpeMin + landmark.rpeMax) / 2);
  if (entry.defaultMeasure === 'time_s') {
    const hold = Math.round((landmark.holdSecondsMin + landmark.holdSecondsMax) / 2);
    return {
      sets: intake.experience === 'beginner' ? 2 : 3,
      repMin: 1,
      repMax: 1,
      targetRpe,
      targetWeightKg: null,
      percent1rm: null,
      tempo: null,
      restSeconds: 20,
      targetDistanceM: null,
      targetDurationS: hold,
      loadMultiplier: null,
      note: `Hold ${hold} s. ${entry.cue} Stay inside a pain-free range.`,
    };
  }
  return {
    sets: 2,
    repMin: 8,
    repMax: 15,
    targetRpe,
    targetWeightKg: null,
    percent1rm: null,
    tempo: '2-0-2-0',
    restSeconds: 45,
    targetDistanceM: null,
    targetDurationS: null,
    loadMultiplier: null,
    note: `Light and controlled. ${entry.cue} Stay inside a pain-free range.`,
  };
}

function prescriptionFor(entry: CatalogueExercise, intake: PlanIntake): PrescriptionCore {
  const isMobilityMovement = entry.pattern === 'mobility' || entry.pattern === 'stability';
  if (intake.track === 'wellness') {
    if (entry.styles.includes('conditioning')) {
      const core = conditioningPrescription(entry, intake);
      const landmark = WELLNESS_LANDMARKS[intake.experience];
      return {
        ...core,
        targetRpe: Math.min(core.targetRpe ?? landmark.rpeMin, landmark.rpeMax),
        note: `${core.note} Stay inside a pain-free range.`,
      };
    }
    return wellnessPrescription(entry, intake);
  }
  // Mobility and stability work is low intensity on every track.
  if (isMobilityMovement) return wellnessPrescription(entry, intake);
  if (intake.track === 'performance' && (entry.pattern === 'plyometric' || entry.pattern === 'throw')) {
    return powerPrescription(entry, intake);
  }
  if (entry.styles.includes('power') && !entry.styles.includes('resistance')) {
    return powerPrescription(entry, intake);
  }
  if (entry.styles.includes('conditioning') && entry.defaultMeasure !== 'reps') {
    return conditioningPrescription(entry, intake);
  }
  if (entry.defaultMeasure !== 'reps') return conditioningPrescription(entry, intake);
  return resistancePrescription(entry, intake, resolveRepProfile(intake.track, intake.goal));
}

const PROGRESSION_LINE: Record<TrainingTrack, string> = {
  strength: 'Double progression: hit the top of the rep range at the target RPE, then add the smallest plate.',
  hypertrophy: 'Double progression: add reps inside the range first, then add the smallest plate.',
  conditioning: 'Add one interval, or keep the intervals and trim 5 s off the rest.',
  performance: 'Add a rep, a jump or a throw — stop the set when output drops.',
  wellness: 'Add 5 s to a hold, or one clean rep, while it stays pain-free.',
  general: 'Double progression: add reps inside the range first, then the smallest plate.',
};

/** Weeks that are deloads: 4, 8, 12 ... up to `weeks`. */
export function deloadWeekNumbers(weeks: number): number[] {
  const out: number[] = [];
  for (let week = DELOAD_WEEK_EVERY; week <= weeks; week += DELOAD_WEEK_EVERY) out.push(week);
  return out;
}

export function isDeloadWeek(week: number, weeks: number): boolean {
  return week > 0 && week <= weeks && week % DELOAD_WEEK_EVERY === 0;
}

export interface DeloadPrescription {
  sets: number;
  targetWeightKg: number | null;
  targetRpe: number | null;
}

/**
 * The deload version of a prescription: volume x0.6 (never below 2 sets) and load
 * -10%, with the target RPE one point lower. Uses the engine's own deload constants.
 */
export function applyDeload(exercise: GeneratedPlanExercise): DeloadPrescription {
  const sets = Math.max(Math.floor(exercise.sets * DELOAD_VOLUME_FACTOR), MIN_DELOAD_SETS);
  const targetWeightKg =
    exercise.targetWeightKg === null
      ? null
      : Math.max(roundToIncrement(exercise.targetWeightKg * DELOAD_INTENSITY_FACTOR, 0.5), 0.5);
  const targetRpe =
    exercise.targetRpe === null ? null : round2(Math.max(5, exercise.targetRpe - 1));
  return { sets, targetWeightKg, targetRpe };
}

// --- session fitting -------------------------------------------------------

const RESISTANCE_CAPS: Record<SessionMinutes, number> = { 30: 4, 45: 5, 60: 6, 75: 7, 90: 8 };
const CONDITIONING_CAPS: Record<SessionMinutes, number> = { 30: 3, 45: 4, 60: 5, 75: 6, 90: 6 };
const MOBILITY_CAPS: Record<SessionMinutes, number> = { 30: 4, 45: 5, 60: 6, 75: 7, 90: 8 };

const WARMUP_MINUTES: Record<TrainingTrack, number> = {
  strength: 8,
  hypertrophy: 6,
  conditioning: 6,
  performance: 8,
  wellness: 3,
  general: 6,
};

function maxExercisesFor(style: DayStyle, sessionMinutes: SessionMinutes): number {
  if (style === 'conditioning') return CONDITIONING_CAPS[sessionMinutes];
  if (style === 'mobility') return MOBILITY_CAPS[sessionMinutes];
  return RESISTANCE_CAPS[sessionMinutes];
}

function shouldSuperset(track: TrainingTrack, sessionMinutes: SessionMinutes): boolean {
  return sessionMinutes <= 45 || (track === 'hypertrophy' && sessionMinutes <= 60);
}

export function estimateExerciseMinutes(exercise: GeneratedPlanExercise): number {
  const restSeconds = exercise.supersetGroup === null ? exercise.restSeconds : exercise.restSeconds * 0.5;
  const workMinutes =
    exercise.measure === 'time_s'
      ? (exercise.targetDurationS ?? 45) / 60
      : exercise.measure === 'amrap'
        ? (exercise.targetDurationS ?? 480) / 60
        : 0.75;
  return exercise.sets * (workMinutes + restSeconds / 60);
}

/** Warm-up plus work, the number the report shows for "about how long". */
export function estimateDayMinutes(day: GeneratedPlanDay, track: TrainingTrack): number {
  const work = day.exercises.reduce((sum, exercise) => sum + estimateExerciseMinutes(exercise), 0);
  return Math.round(WARMUP_MINUTES[track] + work);
}

function isSupersetCandidate(exercise: { isCompound: boolean; pattern: MovementPattern }): boolean {
  return !exercise.isCompound && exercise.pattern !== 'plyometric' && exercise.pattern !== 'throw';
}

/** Superset consecutive accessories; power work is never supersetted. */
function assignSupersets<
  T extends GeneratedPlanExercise & { pattern: MovementPattern; isCompound: boolean },
>(exercises: ReadonlyArray<T>, enabled: boolean): T[] {
  const next = exercises.map((exercise) => ({ ...exercise }));
  if (!enabled) return next.map((exercise) => ({ ...exercise, supersetGroup: null }));
  const pairable = next.filter((exercise) => isSupersetCandidate(exercise));
  for (let k = 0; k + 1 < pairable.length; k += 2) {
    const group = Math.min(1 + Math.floor(k / 2), 12);
    const first = pairable[k];
    const second = pairable[k + 1];
    if (!first || !second) continue;
    first.supersetGroup = group;
    second.supersetGroup = group;
  }
  return next;
}

// `GeneratedPlanExercise` does not carry the selection metadata, so the fitter keeps it locally.
interface PlannedExercise extends GeneratedPlanExercise {
  pattern: MovementPattern;
  isCompound: boolean;
}

function stripPlanningFields(exercise: PlannedExercise): GeneratedPlanExercise {
  const { pattern, isCompound, ...rest } = exercise;
  void pattern;
  void isCompound;
  return rest;
}

function totalMinutes(exercises: ReadonlyArray<GeneratedPlanExercise>): number {
  return exercises.reduce((sum, exercise) => sum + estimateExerciseMinutes(exercise), 0);
}

/** Cap exercises and shrink accessory sets until the session fits the budget. */
function fitSession(
  exercises: ReadonlyArray<PlannedExercise>,
  intake: PlanIntake,
  supersetEnabled: boolean,
): GeneratedPlanExercise[] {
  const budget = intake.sessionMinutes - WARMUP_MINUTES[intake.track];
  const minExercises = intake.sessionMinutes <= 30 ? 2 : 3;
  let list: PlannedExercise[] = exercises.map((exercise) => ({ ...exercise }));

  let guard = 0;
  while (totalMinutes(list) > budget && list.length > minExercises && guard < 50) {
    guard += 1;
    let removeAt = -1;
    for (let index = list.length - 1; index >= 0; index -= 1) {
      const candidate = list[index];
      if (candidate && !candidate.isCompound) {
        removeAt = index;
        break;
      }
    }
    if (removeAt === -1) removeAt = list.length - 1;
    list = list.filter((_, index) => index !== removeAt);
  }

  guard = 0;
  while (totalMinutes(list) > budget && guard < 100) {
    guard += 1;
    let changed = false;
    for (let index = list.length - 1; index >= 0; index -= 1) {
      const candidate = list[index];
      // Respect the session slot: trim any set down to the two-set floor when the
      // budget is tight, accessories first (they are last in the list).
      if (candidate && candidate.sets > MIN_SETS_PER_EXERCISE) {
        list[index] = { ...candidate, sets: candidate.sets - 1 };
        changed = true;
        break;
      }
    }
    if (!changed) break;
  }

  return assignSupersets(list, supersetEnabled).map(stripPlanningFields);
}

// --- determinism -----------------------------------------------------------

/** Canonical field order so the id does not depend on object key order. */
function canonicalIntakeKey(intake: PlanIntake): string {
  return [
    intake.track,
    intake.goal,
    intake.experience,
    String(intake.daysPerWeek),
    String(intake.sessionMinutes),
    String(intake.heightCm),
    String(intake.bodyweightKg),
    String(intake.age),
    intake.sex,
    [...intake.equipment].sort().join('+'),
    [...intake.injuryFlags].sort().join('+'),
    intake.notes ?? '',
  ].join('|');
}

/** FNV-1a, 32-bit, hex — a small deterministic fingerprint of the intake. */
function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function planIdFor(intake: PlanIntake): string {
  return `plan-${intake.track}-${intake.daysPerWeek}d-${fnv1a(canonicalIntakeKey(intake))}`;
}

// --- volume audit ----------------------------------------------------------

const CATALOGUE_BY_NAME = new Map<string, CatalogueExercise>();
for (const entry of EXERCISE_CATALOGUE) {
  CATALOGUE_BY_NAME.set(`${entry.movement}|${entry.variant ?? ''}`, entry);
}

export function catalogueEntryFor(movement: string, variant: string | null): CatalogueExercise | null {
  return CATALOGUE_BY_NAME.get(`${movement}|${variant ?? ''}`) ?? null;
}

export interface MuscleVolumeAudit {
  muscle: ResistanceMuscle;
  sets: number;
  status: VolumeStatus;
}

/** Weekly working sets per resistance muscle, against the landmark bands. */
export function auditWeeklyVolume(plan: GeneratedPlan, experience: Experience): MuscleVolumeAudit[] {
  const landmark = volumeLandmarksFor(experience);
  const totals = new Map<ResistanceMuscle, number>();
  for (const muscle of RESISTANCE_MUSCLES) totals.set(muscle, 0);
  for (const day of plan.days) {
    for (const exercise of day.exercises) {
      const entry = catalogueEntryFor(exercise.movement, exercise.variant);
      const muscle = entry?.muscleGroup;
      if (!muscle) continue;
      if (!(RESISTANCE_MUSCLES as ReadonlyArray<string>).includes(muscle)) continue;
      const key = muscle as ResistanceMuscle;
      totals.set(key, (totals.get(key) ?? 0) + exercise.sets);
    }
  }
  return RESISTANCE_MUSCLES.map((muscle) => {
    const sets = totals.get(muscle) ?? 0;
    return { muscle, sets, status: classifyVolume(sets, landmark) };
  });
}

// --- build -----------------------------------------------------------------

function rotate<T>(items: ReadonlyArray<T>, offset: number): T[] {
  if (items.length === 0) return [];
  const shift = ((offset % items.length) + items.length) % items.length;
  return [...items.slice(shift), ...items.slice(0, shift)];
}

function focusLabels(day: SplitDay): string[] {
  if (day.style === 'conditioning') return ['conditioning'];
  if (day.style === 'power') return ['power'];
  if (day.style === 'mobility') return ['mobility', 'stability'];
  return [...day.focus];
}

function splitSummary(dayList: ReadonlyArray<SplitDay>): string {
  return dayList.map((day) => day.label).join(' / ');
}

function perWeekText(min: number, max: number): string {
  return min === max ? `${min}x per week` : `${min}-${max}x per week`;
}

function experienceArticle(experience: Experience): string {
  return experience === 'advanced' || experience === 'intermediate' ? 'an' : 'a';
}

function buildExercise(selected: SelectedExercise, intake: PlanIntake): PlannedExercise {
  const core = prescriptionFor(selected.entry, intake);
  const deloadSets = Math.max(Math.floor(core.sets * DELOAD_VOLUME_FACTOR), MIN_DELOAD_SETS);
  const deloadWeeks = deloadWeekNumbers(DEFAULT_BLOCK_WEEKS);
  const deloadLine =
    deloadWeeks.length > 0
      ? `Deload week${deloadWeeks.length === 1 ? '' : 's'} ${deloadWeeks.join(' and ')}: ${deloadSets} sets at 90% of the load.`
      : '';
  const notes = [core.note, PROGRESSION_LINE[intake.track], deloadLine]
    .filter((part) => part.length > 0)
    .join(' ');
  return {
    movement: selected.entry.movement,
    variant: selected.entry.variant,
    equipment: selected.entry.equipment,
    sets: core.sets,
    repMin: core.repMin,
    repMax: core.repMax,
    perSide: selected.entry.isUnilateral,
    measure: selected.entry.defaultMeasure,
    targetRpe: core.targetRpe,
    targetWeightKg: core.targetWeightKg,
    percent1rm: core.percent1rm,
    tempo: core.tempo,
    restSeconds: core.restSeconds,
    supersetGroup: null,
    notes,
    isBodyweight: selected.entry.isBodyweight && core.targetWeightKg === null,
    targetDistanceM: core.targetDistanceM,
    targetDurationS: core.targetDurationS,
    pattern: selected.entry.pattern,
    isCompound: selected.entry.isCompound,
  };
}

function buildDay(
  day: SplitDay,
  dayIndex: number,
  intake: PlanIntake,
): GeneratedPlanDay {
  const muscles = rotate(day.focus, dayIndex);
  const selected = selectExercises({
    track: intake.track,
    style: day.style,
    experience: intake.experience,
    equipment: intake.equipment,
    injuryFlags: intake.injuryFlags,
    muscles,
    patterns: day.patterns,
    maxExercises: maxExercisesFor(day.style, intake.sessionMinutes),
    varietyOffset: dayIndex,
  });
  const planned = selected.map((entry) => buildExercise(entry, intake));
  const supersetEnabled = shouldSuperset(intake.track, intake.sessionMinutes);
  const withSupersets = assignSupersets(planned, supersetEnabled);
  const fitted = fitSession(withSupersets, intake, supersetEnabled);
  return {
    dayIndex,
    label: day.label,
    focus: focusLabels(day),
    exercises: fitted,
  };
}

function buildSummary(
  intake: PlanIntake,
  dayList: ReadonlyArray<SplitDay>,
  weeks: number,
  options: PlanBuildOptions,
): string {
  const profile = resolveRepProfile(intake.track, intake.goal);
  const rpe = targetRpeFor(profile, intake.experience);
  const landmark = volumeLandmarksFor(intake.experience);
  const deloads = deloadWeekNumbers(weeks);
  const deloadText =
    deloads.length > 0
      ? `Week${deloads.length === 1 ? '' : 's'} ${deloads.join(' and ')} ${deloads.length === 1 ? 'is a deload' : 'are deloads'}: sets drop to 60% and loads come down 10% so the next block starts fresh.`
      : '';
  const injuryText =
    intake.injuryFlags.length === 0
      ? 'No injury flags, so every pattern in your programme is available.'
      : `We routed around your ${intake.injuryFlags.map((flag) => flag.replace(/_/g, ' ')).join(', ')} flag${intake.injuryFlags.length === 1 ? '' : 's'}: blocked patterns are replaced, never loaded.`;
  const prepared = options.now ? ` Prepared ${options.now}.` : '';

  if (intake.track === 'conditioning') {
    return [
      `Indoor conditioning, ${intake.daysPerWeek} days a week for ${weeks} weeks.`,
      `Every session is gym-based: rower, ski erg, bike erg, sled, carries, ropes and circuits — no running and nothing outdoors.`,
      `Each interval block is prescribed by time or distance at RPE ${rpe}.`,
      `Interval volume builds toward ${trackVolumeTarget('conditioning', intake.experience).target} minutes of hard work a week.`,
      deloadText,
      injuryText,
      `Progress by adding an interval, or keeping the intervals and trimming the rest.${prepared}`,
    ]
      .filter((part) => part.length > 0)
      .join(' ');
  }

  if (intake.track === 'performance') {
    return [
      `Indoor athletic performance, ${intake.daysPerWeek} days a week for ${weeks} weeks.`,
      `Power work comes first while you are fresh — jumps, medicine-ball throws and sled accelerations — then the strength base that raises the ceiling, then short energy-system work.`,
      `Everything is gym-based: boxes, medicine balls, sleds, ergs, racks and platforms. No running and nothing outdoors.`,
      `Strength work sits at ${profile.repMin}-${profile.repMax} reps and RPE ${profile.rpeMin}-${profile.rpeMax}.`,
      deloadText,
      injuryText,
      `Progress by adding a rep, a jump or a throw; stop the set when output drops.${prepared}`,
    ]
      .filter((part) => part.length > 0)
      .join(' ');
  }

  if (intake.track === 'wellness') {
    return [
      `Movement and recovery work, ${intake.daysPerWeek} days a week for ${weeks} weeks.`,
      `Sessions are mobility, stability and light indoor machine or cable work at RPE ${rpe} — no maximal lifts and no PR chasing.`,
      `Every position stays inside a pain-free range.`,
      `Aim for ${WELLNESS_LANDMARKS[intake.experience].mobilitySessionsTarget} sessions a week; consistency is the whole point.`,
      deloadText,
      injuryText,
      `Progress by adding 5 seconds to a hold, or one clean rep, while it stays pain-free.${prepared}`,
    ]
      .filter((part) => part.length > 0)
      .join(' ');
  }

  // strength, hypertrophy and general
  const label = TRACK_LABELS[intake.track];
  const focus =
    intake.track === 'strength'
      ? 'The heavy work leads each session; accessories support it.'
      : intake.track === 'hypertrophy'
        ? 'Each muscle group gets enough weekly sets to grow, and the last sets of a movement are the ones that count.'
        : 'Resistance work builds strength and muscle; the conditioning day trains the engine.';
  return [
    `${label}, ${intake.daysPerWeek} days a week for ${weeks} weeks.`,
    `${splitSummary(dayList)}.`,
    `${focus} Each muscle group lands inside the ${landmark.mev}-${landmark.mrv} set band for ${experienceArticle(intake.experience)} ${intake.experience} lifter, working toward ${targetWeeklySetsFor(intake.experience)} sets.`,
    `Sessions use ${profile.repMin}-${profile.repMax} reps at RPE ${profile.rpeMin}-${profile.rpeMax} with ${profile.restSeconds} s rests.`,
    deloadText,
    injuryText,
    `Progress by double progression: own the top of the rep range, then add the smallest plate.${prepared}`,
  ]
    .filter((part) => part.length > 0)
    .join(' ');
}

/**
 * Build the complete plan. Pure and deterministic: the same intake (and no `now`)
 * always yields byte-identical JSON.
 */
export function buildInitialPlan(intake: PlanIntake, options: PlanBuildOptions = {}): GeneratedPlan {
  const parsed = PlanIntakeSchema.parse(intake);
  const weeks = DEFAULT_BLOCK_WEEKS;
  const table = SPLITS[parsed.track];
  const dayList = table[parsed.daysPerWeek];
  const days = dayList.map((day, index) => buildDay(day, index, parsed));

  const profile = resolveRepProfile(parsed.track, parsed.goal);
  const landmark = volumeLandmarksFor(parsed.experience);
  const frequency = frequencyRuleFor(parsed.daysPerWeek);
  const volumeTarget = trackVolumeTarget(parsed.track, parsed.experience);
  const deloads = deloadWeekNumbers(weeks);
  const blocked = blockedExercises({
    track: parsed.track,
    equipment: parsed.equipment,
    injuryFlags: parsed.injuryFlags,
  });
  const blockedFamilies = [...new Set(blocked.map((item) => item.hit.family))].sort();
  const estimated = days
    .flatMap((day) => day.exercises)
    .filter((exercise) => exercise.targetWeightKg !== null).length;
  const estimatedMultipliers = days
    .flatMap((day) => day.exercises)
    .map((exercise) => {
      const entry = catalogueEntryFor(exercise.movement, exercise.variant);
      if (!entry) return null;
      return exercise.targetWeightKg !== null ? PATTERN_LOAD_MULTIPLIER[entry.pattern] ?? null : null;
    })
    .filter((value): value is number => value !== null);
  const multiplierSpread = [
    ...new Set(estimatedMultipliers.map((value) => value.toFixed(2))),
  ].sort();

  const rationale: Array<{ rule: string; evidence: string }> = [];
  rationale.push({
    rule: `track.${parsed.track}`,
    evidence: `track=${parsed.track} (goal=${parsed.goal}) → metricsFocus=[${TRACK_METRICS_FOCUS[parsed.track].join(', ')}]; ${volumeTarget.note}`,
  });
  rationale.push({
    rule: `split.${parsed.track}_${parsed.daysPerWeek}day`,
    evidence: `daysPerWeek=${parsed.daysPerWeek}, track=${parsed.track} → ${splitSummary(dayList)}. ${frequency.note}`,
  });
  rationale.push({
    rule: `volume.${volumeTarget.unit}`,
    evidence:
      volumeTarget.unit === 'sets_per_muscle_per_week'
        ? `experience=${parsed.experience} → MEV ${landmark.mev} / MAV ${landmark.mavMin}-${landmark.mavMax} / MRV ${landmark.mrv} sets per muscle per week; target ${volumeTarget.target}. Each muscle group is trained ${perWeekText(frequency.sessionsPerMuscleMin, frequency.sessionsPerMuscleMax)}.`
        : `experience=${parsed.experience} → ${volumeTarget.unit} minimum ${volumeTarget.minimum}, target ${volumeTarget.target}, maximum ${volumeTarget.maximum}. ${volumeTarget.note}`,
  });
  rationale.push({
    rule: parsed.track === 'general' ? `reps.goal_${parsed.goal}` : `reps.track_${parsed.track}`,
    evidence: `track=${parsed.track}${parsed.track === 'general' ? `, goal=${parsed.goal}` : ''} → ${profile.repMin}-${profile.repMax} reps at RPE ${profile.rpeMin}-${profile.rpeMax}, ${profile.restSeconds} s rest.`,
  });
  rationale.push({
    rule: 'exercise_selection.compound_first_indoor',
    evidence: `equipment=[${[...parsed.equipment].join(', ')}] → compound movements first, accessories after; conditioning by time/distance; every movement is gym-based and indoor.`,
  });
  rationale.push({
    rule: parsed.injuryFlags.length === 0 ? 'injuries.none' : `injuries.${parsed.injuryFlags.join('+')}`,
    evidence:
      parsed.injuryFlags.length === 0
        ? 'injuryFlags=[] → no movement patterns blocked; every catalogue entry on this track is available.'
        : `injuryFlags=[${parsed.injuryFlags.join(', ')}] → blocked ${blocked.length} catalogue movement(s) across ${blockedFamilies.length} pattern family(ies) (${blockedFamilies.join(', ') || 'none on this track'}); replacements come from the same muscle group and are never loaded.`,
  });
  rationale.push({
    rule: estimated > 0 ? 'load.starting_estimate' : 'load.first_session_rpe',
    evidence:
      estimated > 0
        ? `bodyweightKg=${parsed.bodyweightKg}, sex=${parsed.sex}, experience=${parsed.experience} → start loads = bodyweight × pattern multiplier (${multiplierSpread.join(', ') || 'n/a'}) × experience factor × sex factor, a heuristic with about ±${ESTIMATE_UNCERTAINTY_PCT}% uncertainty. ${estimated} compound movement(s) estimated; the rest start at "first session: find your RPE ${targetRpeFor(profile, parsed.experience)}".`
        : `bodyweightKg=${parsed.bodyweightKg} and the selected movements did not allow a defensible start load → every load is left open with "first session: find your RPE ${targetRpeFor(profile, parsed.experience)}".`,
  });
  rationale.push({
    rule: 'deload.every_4th_week',
    evidence: `weeks=${weeks} → deload week(s) ${deloads.join(', ')}; volume ×${DELOAD_VOLUME_FACTOR}, load ×${DELOAD_INTENSITY_FACTOR} (−${Math.round((1 - DELOAD_INTENSITY_FACTOR) * 100)}%).`,
  });

  return {
    planId: planIdFor(parsed),
    name: `${TRACK_LABELS[parsed.track]} · ${splitSummary(dayList)}`,
    goal: parsed.goal,
    track: parsed.track,
    weeks,
    daysPerWeek: parsed.daysPerWeek,
    summaryMd: buildSummary(parsed, dayList, weeks, options),
    metricsFocus: [...TRACK_METRICS_FOCUS[parsed.track]],
    rationale,
    days,
  };
}
